import React from 'react';
import { NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import AppStoreButton from '@components/atoms/app-store-button/app-store-button';
import clsx from 'clsx';
import extractFromContext from '@helpers/utils/extract-from-context';
import VcImage from '@components/atoms/vc-images/vc-image';
import DefaultLayout from '@layouts/default-layout';
import styles from './index.module.scss';

const ApplicationDownloadButtons = () => {
  return (
    <>
      <AppStoreButton
        platform="android"
        url="https://app.adjust.com/2ep0vf"
        size={{ height: 50 }}
        className={styles.applications__appStoreBtn}
      />
      <AppStoreButton
        platform="ios"
        url="https://app.adjust.com/xa7pe2"
        size={{ height: 50 }}
        className={styles.applications__appStoreBtn}
      />
    </>
  );
};

const ApplicationsPage: NextPageWithLayout = () => {
  const { t } = useTranslation('applications');

  return (
    <>
      <main className={clsx('innerContainer', styles.applications)}>
        <div className={clsx(styles.applications__header)}>
          <VcImage
            className={styles.applications__header__image}
            sizes="980px"
            src={`/images/pages/landings/application/header_application_page.png`}
            alt=""
            width="980"
            height="361"
            shouldUseAssetImage
          />
          <div className={styles.applications__header__blockText}>
            <h1 className={clsx('vc-title-s', styles.applications__header__blockText__title)}>
              {t('APPLICATIONS.HEADER.TITLE')}
            </h1>
            <h2 className={clsx('vc-text-l', styles.applications__header__blockText__subTitle)}>
              {t('APPLICATIONS.HEADER.SUB_TITLE')}
            </h2>
            <div className={styles.applications__header__blockText__buttonsWrapper}>
              <ApplicationDownloadButtons />
            </div>
          </div>
        </div>
        <section className={clsx('d-md-flex', styles.applications__section, styles.applications__section__reverse)}>
          <div className={styles.applications__section__blockText}>
            <h1 className={clsx('vc-text-xl', styles.applications__section__blockText__title)}>
              {t('APPLICATIONS.SECTION_1.TITLE')}
            </h1>
            <div className={clsx('d-md-flex', styles.applications__section__blockText__itemContainer)}>
              <div className={clsx(styles.applications__section__blockText__item)}>
                <div className={clsx(styles.applications__section__blockText__icon)}>
                  <VcImage
                    src={`/svg/bag-line.svg`}
                    alt=""
                    width="60"
                    height="60"
                    shouldUseAssetImage
                  />
                </div>
                <div className={clsx('vc-text-l', styles.applications__section__blockText__subTitle)}>
                  {t('APPLICATIONS.SECTION_1.SUB_TITLE_1')}
                </div>
                <div className="vc-text-m">{t('APPLICATIONS.SECTION_1.DESC_1')}</div>
              </div>
              <div className={clsx(styles.applications__section__blockText__item)}>
                <div className={clsx(styles.applications__section__blockText__icon)}>
                  <VcImage
                    sizes="60px"
                    src={`/svg/phone-line.svg`}
                    alt=" "
                    width="60"
                    height="60"
                    shouldUseAssetImage
                  />
                </div>

                <div className={clsx('vc-text-m', styles.applications__section__blockText__subTitle)}>
                  {t('APPLICATIONS.SECTION_1.SUB_TITLE_2')}
                </div>
                <div className="vc-text-m">{t('APPLICATIONS.SECTION_1.DESC_2')}</div>
              </div>
            </div>
          </div>
          <div className={styles.applications__section__image}>
            <VcImage
              src={`/images/pages/landings/application/iphone-ipad_app.jpg`}
              alt=""
              width="446"
              height="309"
              shouldUseAssetImage
            />
          </div>
        </section>
        <section className={clsx(styles.applications__section, 'd-md-flex')}>
          <div className={styles.applications__section__blockText}>
            <h1 className={clsx('vc-text-xl', styles.applications__section__blockText__title)}>
              {t('APPLICATIONS.SECTION_2.TITLE')}
            </h1>
            <div className={clsx('d-md-flex', styles.applications__section__blockText__itemContainer)}>
              <div className={clsx(styles.applications__section__blockText__item)}>
                <div className={clsx(styles.applications__section__blockText__icon)}>
                  <VcImage
                    sizes="60px"
                    src={`/svg/phoneLandscape-line.svg`}
                    alt=" "
                    width="60"
                    height="60"
                    shouldUseAssetImage
                  />
                </div>
                <div className={clsx('vc-text-l', styles.applications__section__blockText__subTitle)}>
                  {t('APPLICATIONS.SECTION_2.SUB_TITLE_1')}
                </div>
                <div className="vc-text-m">{t('APPLICATIONS.SECTION_2.DESC_1')}</div>
              </div>
              <div className={clsx(styles.applications__section__blockText__item)}>
                <div className={clsx(styles.applications__section__blockText__icon)}>
                  <VcImage
                    sizes="60px"
                    src={`/svg/chatBox-line.svg`}
                    alt=" "
                    width="60"
                    height="60"
                    shouldUseAssetImage
                  />
                </div>

                <div className={clsx('vc-text-l', styles.applications__section__blockText__subTitle)}>
                  {t('APPLICATIONS.SECTION_2.SUB_TITLE_2')}
                </div>
                <div className="vc-text-m">{t('APPLICATIONS.SECTION_2.DESC_2')}</div>
              </div>
            </div>
          </div>
          <div className={styles.applications__section__image}>
            <VcImage
              src={`/images/pages/landings/application/nexus-iphone_app.jpg`}
              alt=""
              width="328"
              height="374"
              shouldUseAssetImage
            />
          </div>
        </section>
        <div className={styles.applications__downloadBlock}>
          <h1 className={clsx('vc-title-s', styles.applications__downloadBlock__title)}>
            {t('APPLICATIONS.FOOTER.TITLE')}
          </h1>
          <div className={styles.applications__downloadBlock__buttonsWrapper}>
            <ApplicationDownloadButtons />
          </div>
        </div>
      </main>
    </>
  );
};

ApplicationsPage.Layout = [DefaultLayout];
ApplicationsPage.PageType = 'ApplicationsPage';

export async function getServerSideProps(context) {
  const { locale } = extractFromContext(context);

  const translations = await serverSideTranslations(locale, ['default-layout', 'applications', 'common']);

  return {
    props: {
      ...translations,
    },
  };
}

export default ApplicationsPage;
